import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plug, CheckCircle2, FlaskConical, Loader2, XCircle,
  ChevronLeft, ChevronRight, Slack, MessageSquare, Mail, Radar, Database,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RequireRole } from '@/components/auth/RequireRole';
import { cn } from '@/lib/utils';
import {
  useAvailableIntegrations,
  useIntegrations,
  useCreateIntegration,
  useUpdateIntegration,
  useDeleteIntegration,
  useTestIntegration,
} from '@/api/hooks/useSettings';
import type { AvailableIntegration, Integration } from '@/api/types/settings.types';

// ── Well-known field definitions ──────────────────────────────────────────────

type FieldDef = { key: string; label: string; type: string; required: boolean; placeholder?: string };

const KNOWN_FIELDS: Record<string, FieldDef[]> = {
  slack: [
    { key: 'webhook_url', label: 'Webhook URL', type: 'url', required: true, placeholder: 'https://hooks.slack.com/services/...' },
  ],
  teams: [
    { key: 'webhook_url', label: 'Webhook URL', type: 'url', required: true, placeholder: 'https://outlook.office.com/webhook/...' },
  ],
  jira: [
    { key: 'base_url', label: 'Base URL', type: 'url', required: true, placeholder: 'https://yourcompany.atlassian.net' },
    { key: 'project_key', label: 'Project Key', type: 'text', required: true, placeholder: 'GRC' },
    { key: 'email', label: 'Account Email', type: 'text', required: true, placeholder: 'user@company.com' },
    { key: 'api_token', label: 'API Token', type: 'password', required: true },
  ],
  servicenow: [
    { key: 'instance_url', label: 'Instance URL', type: 'url', required: true, placeholder: 'https://yourinstance.service-now.com' },
    { key: 'username', label: 'Username', type: 'text', required: true },
    { key: 'password', label: 'Password', type: 'password', required: true },
  ],
  pagerduty: [
    { key: 'api_key', label: 'API Key', type: 'password', required: true },
    { key: 'service_id', label: 'Service ID', type: 'text', required: true },
  ],
};

function getFields(avail: AvailableIntegration): FieldDef[] {
  return KNOWN_FIELDS[avail.integration_type] ?? (avail.config_fields ?? []);
}

// ── Configure Modal (connected integration) ───────────────────────────────────

function ConfigureModal({
  available,
  configured,
  onClose,
}: {
  available: AvailableIntegration;
  configured: Integration;
  onClose: () => void;
}) {
  const updateMutation = useUpdateIntegration();
  const deleteMutation = useDeleteIntegration();
  const testMutation = useTestIntegration();

  const fields = getFields(available);
  const [config, setConfig] = useState<Record<string, string>>((configured as any).config ?? {});
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    updateMutation.mutate(
      { id: configured.id, config },
      {
        onSuccess: () => { toast.success('Integration updated'); onClose(); },
        onError: () => toast.error('Failed to update integration'),
      },
    );
  }

  function handleTest() {
    setTestResult(null);
    testMutation.mutate(configured.id, {
      onSuccess: (result) => {
        setTestResult({
          success: result.success,
          message: result.success
            ? (result.message || 'Connection successful! Test message sent.')
            : (result.message || 'Connection failed. Please check your settings.'),
        });
      },
      onError: () => setTestResult({ success: false, message: 'Connection failed. Please check your settings.' }),
    });
  }

  function handleDisconnect() {
    deleteMutation.mutate(configured.id, {
      onSuccess: () => { toast.success(`${configured.display_name} disconnected`); onClose(); },
      onError: () => toast.error('Failed to disconnect integration'),
    });
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{available.display_name} Settings</DialogTitle>
          <DialogDescription className="sr-only">Configure integration settings</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-4">
          {fields.length === 0 && (
            <p className="text-xs text-text-secondary italic">No configuration required.</p>
          )}

          {fields.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <Label>
                {field.label}
                {field.required && <span className="text-accent-rose ml-1">*</span>}
              </Label>
              <Input
                type={field.type === 'password' ? 'password' : field.type === 'url' ? 'url' : 'text'}
                placeholder={field.placeholder}
                value={config[field.key] ?? ''}
                onChange={(e) => setConfig((c) => ({ ...c, [field.key]: e.target.value }))}
                required={field.required}
              />
            </div>
          ))}

          {/* Test Connection — full width */}
          <div className="space-y-2 pt-1">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleTest}
              disabled={testMutation.isPending}
            >
              {testMutation.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Testing Connection…</>
              ) : (
                <><FlaskConical className="h-4 w-4 mr-2" />Test Connection</>
              )}
            </Button>

            {/* Inline test result */}
            {testResult && (
              <div className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${
                testResult.success
                  ? 'bg-accent-emerald/10 text-accent-emerald border border-accent-emerald/20'
                  : 'bg-accent-rose/10 text-accent-rose border border-accent-rose/20'
              }`}>
                {testResult.success
                  ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                  : <XCircle className="h-4 w-4 shrink-0 mt-0.5" />}
                <span>{testResult.message}</span>
              </div>
            )}
          </div>

          {/* Cancel + Save */}
          <div className="flex items-center justify-between pt-1 border-t border-border-subtle">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" />Saving…</>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>

        {/* Divider + disconnect */}
        <div className="border-t border-border-subtle pt-4 space-y-3">
          {confirmingDisconnect ? (
            <div className="rounded-lg border border-accent-rose/30 bg-accent-rose/5 p-3 space-y-2">
              <p className="text-sm text-text-primary font-medium">
                Disconnect {available.display_name}?
              </p>
              <p className="text-xs text-text-secondary">
                This will remove the integration and stop all syncs. You can reconnect at any time.
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmingDisconnect(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                  disabled={deleteMutation.isPending}
                  onClick={handleDisconnect}
                >
                  {deleteMutation.isPending ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Disconnecting…</>
                  ) : (
                    'Confirm'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-accent-rose hover:text-accent-rose hover:bg-accent-rose/10"
              onClick={() => setConfirmingDisconnect(true)}
            >
              Disconnect Integration
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Setup Modal (new connection) ──────────────────────────────────────────────

function SetupModal({
  available,
  onClose,
}: {
  available: AvailableIntegration;
  onClose: () => void;
}) {
  const createMutation = useCreateIntegration();
  const fields = getFields(available);
  const [config, setConfig] = useState<Record<string, string>>({});

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate(
      { integrationType: available.integration_type, displayName: available.display_name, config },
      {
        onSuccess: () => { toast.success(`${available.display_name} connected`); onClose(); },
        onError: () => toast.error('Failed to configure integration'),
      },
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Connect {available.display_name}</DialogTitle>
          <DialogDescription className="sr-only">Configure integration settings</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-text-secondary">{available.description}</p>

          {fields.length === 0 && (
            <p className="text-xs text-text-secondary italic">No configuration required.</p>
          )}

          {fields.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <Label>
                {field.label}
                {field.required && <span className="text-accent-rose ml-1">*</span>}
              </Label>
              <Input
                type={field.type === 'password' ? 'password' : field.type === 'url' ? 'url' : 'text'}
                placeholder={field.placeholder}
                value={config[field.key] ?? ''}
                onChange={(e) => setConfig((c) => ({ ...c, [field.key]: e.target.value }))}
                required={field.required}
              />
            </div>
          ))}

          <div className="flex justify-between pt-2 border-t border-border-subtle">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" />Connecting…</>
              ) : (
                'Connect'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Section grouping (frontend map over the catalog `category`) ─────────────────

type IntegrationGroup = 'Notifications & Messaging' | 'Monitoring & Storage';

const GROUP_ORDER: IntegrationGroup[] = ['Notifications & Messaging', 'Monitoring & Storage'];

const CATEGORY_TO_GROUP: Record<string, IntegrationGroup> = {
  Messaging: 'Notifications & Messaging',
  Notifications: 'Notifications & Messaging',
  SIEM: 'Monitoring & Storage',
  'Cloud Storage': 'Monitoring & Storage',
};

// Per-section icon-chip hue + hover accent (existing accent tokens; literal classes
// so Tailwind's JIT emits them). Mirrors HUB-2's CATEGORY_STYLE pattern.
const GROUP_STYLE: Record<IntegrationGroup, { chip: string; hoverBorder: string }> = {
  'Notifications & Messaging': { chip: 'bg-accent-violet-dim text-accent-violet', hoverBorder: 'group-hover:border-accent-violet/50' },
  'Monitoring & Storage':      { chip: 'bg-accent-sky-dim text-accent-sky',       hoverBorder: 'group-hover:border-accent-sky/50' },
};

const INTEGRATION_ICON: Record<string, React.ElementType> = {
  slack: Slack,
  teams: MessageSquare,
  email: Mail,
  splunk: Radar,
  s3: Database,
};

// ── Tile ──────────────────────────────────────────────────────────────────────

function IntegrationTile({
  avail,
  configured,
  onAction,
}: {
  avail: AvailableIntegration;
  configured?: Integration;
  onAction: () => void;
}) {
  const group = CATEGORY_TO_GROUP[avail.category];
  const style = GROUP_STYLE[group];
  const Icon = INTEGRATION_ICON[avail.integration_type] ?? Plug;

  const head = (
    <>
      <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', style.chip)}>
        <Icon className="h-5 w-5" />
      </div>
    </>
  );

  // Coming-soon: non-navigable, muted, dashed border + badge, NO Setup action.
  // All five are coming-soon today (catalog `coming_soon = TRUE`).
  if (avail.coming_soon) {
    return (
      <div className="relative flex items-start gap-3.5 rounded-2xl border border-dashed border-border-subtle bg-bg-surface/60 p-5 opacity-70">
        {head}
        <div className="min-w-0 flex-1 pr-20">
          <p className="font-semibold text-text-primary">{avail.display_name}</p>
          <p className="mt-1 text-xs leading-relaxed text-text-secondary">{avail.description}</p>
        </div>
        <span className="absolute right-3.5 top-3.5 rounded-full border border-border-subtle bg-bg-surface-2 px-2 py-0.5 text-[10px] font-semibold text-text-muted">
          Coming soon
        </span>
      </div>
    );
  }

  // Dormant actionable branch (coming_soon === false) — the future-live pattern.
  // Not reached today; the Setup/Configure modal wires up when an integration ships.
  const connected = configured?.is_active;
  return (
    <button
      type="button"
      onClick={onAction}
      className={cn(
        'group relative flex w-full items-start gap-3.5 rounded-2xl border border-border-subtle bg-bg-surface p-5 text-left transition-all duration-200',
        'hover:-translate-y-[3px] hover:bg-bg-surface-2 hover:shadow-lg',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan',
        style.hoverBorder,
      )}
    >
      {head}
      <div className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="font-semibold text-text-primary">{avail.display_name}</span>
          {connected && (
            <span className="inline-flex items-center gap-1 rounded-full border border-accent-emerald/30 bg-accent-emerald/10 px-2 py-0.5 text-[10px] font-semibold text-accent-emerald">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-emerald" />
              Connected
            </span>
          )}
        </span>
        <p className="mt-1 text-xs leading-relaxed text-text-secondary">{avail.description}</p>
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-text-primary">
          {configured ? 'Manage' : 'Set up'}
          <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </button>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function IntegrationsPage() {
  return (
    <RequireRole roles={['risk_manager', 'super_admin']} label="Integrations">
      <IntegrationsPageBody />
    </RequireRole>
  );
}

function IntegrationsPageBody() {
  const { data: available, isLoading: availLoading } = useAvailableIntegrations();
  const { data: configured } = useIntegrations();

  const [modalTarget, setModalTarget] = useState<AvailableIntegration | null>(null);

  const configuredMap = new Map(configured?.map((i) => [i.integration_type, i]) ?? []);

  const groups = GROUP_ORDER.map((group) => ({
    group,
    tiles: (available ?? []).filter((a) => CATEGORY_TO_GROUP[a.category] === group),
  })).filter((g) => g.tiles.length > 0);

  return (
    <div className="mx-auto max-w-6xl space-y-9">
      <Link
        to="/settings"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
      >
        <ChevronLeft className="h-4 w-4" />
        All Settings
      </Link>

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-secondary">Settings</div>
        <h1 className="mt-2 text-2xl font-bold text-text-primary">Integrations</h1>
        <p className="mt-1 text-sm text-text-secondary">Connect VeriGRC to the tools your team relies on.</p>
      </div>

      {availLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
      ) : (
        groups.map(({ group, tiles }) => (
          <section key={group} className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-secondary">{group}</span>
              <span className="rounded-full border border-border-subtle bg-bg-surface px-2 py-px text-[11px] font-semibold text-text-muted">
                {tiles.length}
              </span>
              <span className="h-px flex-1 bg-gradient-to-r from-border-subtle to-transparent" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tiles.map((avail) => (
                <IntegrationTile
                  key={avail.integration_type}
                  avail={avail}
                  configured={configuredMap.get(avail.integration_type)}
                  onAction={() => setModalTarget(avail)}
                />
              ))}
            </div>
          </section>
        ))
      )}

      <div className="border-t border-border-subtle pt-5 text-sm text-text-secondary">
        More integrations are on the way. Tell us what you'd like to see at{' '}
        <a href="mailto:support@verigrc.com" className="font-medium text-accent-cyan hover:underline">
          support@verigrc.com
        </a>
        .
      </div>

      {/* Setup/Configure modals — dormant; only reached when a tile is actionable
          (coming_soon === false). Retained for when integrations go live. */}
      {modalTarget && (() => {
        const conn = configuredMap.get(modalTarget.integration_type);
        return conn ? (
          <ConfigureModal
            available={modalTarget}
            configured={conn}
            onClose={() => setModalTarget(null)}
          />
        ) : (
          <SetupModal
            available={modalTarget}
            onClose={() => setModalTarget(null)}
          />
        );
      })()}
    </div>
  );
}
